'use client';

import { useState, useEffect } from 'react';
import ProjectCard from './ProjectCard';
import FilterBar from './FilterBar';
import ProjectModal from './ProjectModal';
import StatsSection from './StatsSection';
import Icon from '@/components/ui/AppIcon';


interface Project {
    id: number;
    title: string;
    category: string;
    description: string;
    fullDescription: string;
    image: string;
    alt: string;
    liveUrl: string;
    metrics: {
        trafficIncrease: string;
        rankingImprovement: string;
        conversionBoost: string;
    };
    technologies: string[];
    timeline: string;
    client: string;
    challenges: string[];
    solutions: string[];
    results: string[];
    testimonial: {
        text: string;
        author: string;
        position: string;
        avatar: string;
        avatarAlt: string;
    };
}

const mockProjects: Project[] = [
    {
        id: 1,
        title: "Pincode Hotels - Luxury Hospitality Platform",
        category: "Web Development",
        description: "Complete digital transformation for a premium hotel chain with integrated booking system and brand visibility optimization.",
        fullDescription: "Pincode Hotels required a sophisticated web platform that would showcase their luxury properties while providing seamless booking experiences. We developed a full-stack solution with advanced search capabilities, real-time availability checking, and integrated payment processing. The platform features stunning visual galleries, interactive room tours, and a comprehensive content management system for easy updates across all properties.",
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_14ae2d1ed-1765211766415.png",
        alt: "Luxury hotel lobby with modern chandelier, marble floors, and elegant seating areas",
        liveUrl: "https://pincodehotels.example.com",
        metrics: {
            trafficIncrease: "+245%",
            rankingImprovement: "#1",
            conversionBoost: "+180%"
        },
        technologies: ["Next.js", "TypeScript", "Tailwind CSS", "Node.js", "PostgreSQL", "Stripe API", "Google Maps API"],
        timeline: "6 months",
        client: "Pincode Hotels Group",
        challenges: [
            "Complex multi-property booking system with real-time availability",
            "Integration with legacy property management systems",
            "High-resolution image optimization for fast loading",
            "Multi-currency payment processing across regions"],

        solutions: [
            "Custom booking engine with Redis caching for instant availability checks",
            "API middleware layer for seamless legacy system integration",
            "Progressive image loading with WebP format and CDN distribution",
            "Stripe Connect implementation for multi-currency support"],

        results: [
            "245% increase in organic traffic within 6 months",
            "Achieved #1 ranking for 'luxury hotels in Mumbai' and related keywords",
            "180% boost in direct booking conversions",
            "Reduced page load time from 8.5s to 1.2s",
            "98% customer satisfaction rating for booking experience"],

        testimonial: {
            text: "Site Map Genius transformed our digital presence completely. The new platform not only looks stunning but has dramatically increased our direct bookings. Their understanding of both technology and hospitality industry needs is exceptional.",
            author: "Rajesh Sharma",
            position: "CEO, Pincode Hotels Group",
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18141ae6d-1763293994835.png",
            avatarAlt: "Professional headshot of Indian businessman in navy suit with confident smile"
        }
    },
    {
        id: 2,
        title: "AK47Sports - E-commerce Sports Platform",
        category: "E-commerce",
        description: "High-performance e-commerce platform for sports equipment with advanced inventory management and SEO optimization.",
        fullDescription: "AK47Sports needed a robust e-commerce solution to compete in the competitive sports equipment market. We built a scalable platform with advanced product filtering, size guides, inventory tracking, and seamless checkout experience. The platform includes customer reviews, wishlist functionality, and personalized product recommendations based on browsing history and purchase patterns.",
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0a04008-1764827205574.png",
        alt: "Modern sports equipment store interior with running shoes, athletic gear, and fitness accessories displayed on white shelves",
        liveUrl: "https://ak47sports.example.com",
        metrics: {
            trafficIncrease: "+320%",
            rankingImprovement: "Top 3",
            conversionBoost: "+215%"
        },
        technologies: ["React", "Next.js", "TypeScript", "MongoDB", "Express.js", "Razorpay", "AWS S3"],
        timeline: "5 months",
        client: "AK47Sports Pvt Ltd",
        challenges: [
            "Managing complex product variants (sizes, colors, materials)",
            "Real-time inventory synchronization across multiple warehouses",
            "Implementing advanced search with filters and facets",
            "Optimizing for mobile shopping experience"],

        solutions: [
            "Dynamic product variant system with SKU-based inventory tracking",
            "WebSocket implementation for real-time stock updates",
            "Elasticsearch integration for lightning-fast product search",
            "Progressive Web App (PWA) for app-like mobile experience"],

        results: [
            "320% increase in organic search traffic",
            "Achieved top 3 rankings for 50+ sports equipment keywords",
            "215% improvement in conversion rate",
            "Average order value increased by 45%",
            "Mobile conversion rate improved by 180%"],

        testimonial: {
            text: "The e-commerce platform Site Map Genius built for us exceeded all expectations. Our online sales have tripled, and the SEO work has put us ahead of competitors who've been in the market much longer. Highly recommended!",
            author: "Arjun Kumar",
            position: "Founder & Director, AK47Sports",
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1517d7869-1763294003686.png",
            avatarAlt: "Young Indian entrepreneur in casual blue shirt with friendly smile in modern office"
        }
    },
    {
        id: 3,
        title: "TechVista Solutions - Corporate Website",
        category: "Web Development",
        description: "Modern corporate website with interactive service showcases and lead generation optimization.",
        fullDescription: "TechVista Solutions required a professional corporate presence that would effectively communicate their IT consulting services while generating quality leads. We created a sophisticated website with interactive service demonstrations, case study showcases, and an intelligent lead qualification system. The platform features dynamic content personalization based on visitor industry and interests.",
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_123d4f5a5-1765003071043.png",
        alt: "Modern corporate office workspace with glass walls, collaborative meeting areas, and contemporary furniture",
        liveUrl: "https://techvista.example.com",
        metrics: {
            trafficIncrease: "+190%",
            rankingImprovement: "#2",
            conversionBoost: "+165%"
        },
        technologies: ["Next.js", "TypeScript", "Tailwind CSS", "Sanity CMS", "HubSpot API", "Google Analytics 4"],
        timeline: "4 months",
        client: "TechVista Solutions",
        challenges: [
            "Communicating complex technical services to non-technical audiences",
            "Implementing effective lead scoring and qualification",
            "Creating engaging interactive service demonstrations",
            "Integrating with existing CRM and marketing automation tools"],

        solutions: [
            "Simplified service explanations with interactive infographics and animations",
            "Custom lead scoring algorithm based on engagement metrics",
            "WebGL-based interactive demos for key service offerings",
            "Seamless HubSpot integration for automated lead nurturing"],

        results: [
            "190% increase in qualified lead generation",
            "Achieved #2 ranking for 'IT consulting services India'",
            "165% improvement in lead-to-customer conversion",
            "Average time on site increased by 240%",
            "Reduced cost per lead by 55%"],

        testimonial: {
            text: "Site Map Genius didn't just build us a website; they created a lead generation machine. The quality of inquiries has improved dramatically, and our sales team loves the integrated CRM workflow.",
            author: "Priya Mehta",
            position: "Marketing Director, TechVista Solutions",
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_124bbe54a-1763295891998.png",
            avatarAlt: "Professional Indian woman in business attire with confident expression in corporate setting"
        }
    },
    {
        id: 4,
        title: "FitLife Wellness - Mobile App",
        category: "Mobile Development",
        description: "Comprehensive fitness and wellness mobile application with personalized workout plans and nutrition tracking.",
        fullDescription: "FitLife Wellness wanted to create a holistic health and fitness mobile application that would stand out in a crowded market. We developed a feature-rich Android application with AI-powered workout recommendations, nutrition tracking, progress analytics, and social community features. The app includes video workout libraries, meal planning tools, and integration with popular fitness wearables.",
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b621f478-1764872610910.png",
        alt: "Fitness enthusiasts working out in modern gym with exercise equipment, weights, and yoga mats",
        liveUrl: "https://play.google.com/store/apps/fitlife",
        metrics: {
            trafficIncrease: "+280%",
            rankingImprovement: "Top 5",
            conversionBoost: "+195%"
        },
        technologies: ["React Native", "TypeScript", "Firebase", "TensorFlow Lite", "Google Fit API", "Stripe"],
        timeline: "7 months",
        client: "FitLife Wellness Pvt Ltd",
        challenges: [
            "Creating personalized workout plans using machine learning",
            "Ensuring smooth video playback for workout demonstrations",
            "Syncing data across multiple devices and wearables",
            "Implementing offline functionality for gym environments"],

        solutions: [
            "TensorFlow Lite integration for on-device AI recommendations",
            "Adaptive bitrate streaming with local video caching",
            "Cloud Firestore for real-time multi-device synchronization",
            "Progressive data sync with offline-first architecture"],

        results: [
            "50,000+ downloads in first 3 months",
            "4.8-star rating on Google Play Store",
            "280% increase in app store visibility",
            "195% improvement in premium subscription conversions",
            "Average daily active users: 15,000+"],

        testimonial: {
            text: "The FitLife app has exceeded our wildest expectations. Site Map Genius's technical expertise combined with their understanding of the fitness industry created something truly special. Our user retention rates are industry-leading.",
            author: "Vikram Singh",
            position: "Co-founder, FitLife Wellness",
            avatar: "https://images.unsplash.com/photo-1623491278146-20646a93d849",
            avatarAlt: "Fit Indian man in athletic wear with determined expression in gym environment"
        }
    },
    {
        id: 5,
        title: "GreenEarth Organics - E-commerce Platform",
        category: "E-commerce",
        description: "Sustainable organic products marketplace with subscription management and local delivery optimization.",
        fullDescription: "GreenEarth Organics needed an e-commerce platform that reflected their commitment to sustainability while providing convenient shopping experiences. We built a comprehensive marketplace with subscription box options, local farm partnerships, delivery route optimization, and educational content about organic farming. The platform includes carbon footprint tracking for each order and community engagement features.",
        image: "https://images.unsplash.com/photo-1712574030534-c6724847acc4",
        alt: "Fresh organic vegetables and fruits displayed in wooden crates at farmers market with green leafy produce",
        liveUrl: "https://greenearth.example.com",
        metrics: {
            trafficIncrease: "+265%",
            rankingImprovement: "#1",
            conversionBoost: "+220%"
        },
        technologies: ["Next.js", "TypeScript", "PostgreSQL", "Redis", "Mapbox API", "Razorpay", "SendGrid"],
        timeline: "5 months",
        client: "GreenEarth Organics",
        challenges: [
            "Managing perishable inventory with short shelf lives",
            "Optimizing delivery routes for freshness and efficiency",
            "Implementing flexible subscription management",
            "Educating customers about organic products and sustainability"],

        solutions: [
            "Dynamic inventory system with expiration date tracking and alerts",
            "AI-powered route optimization using Mapbox for same-day delivery",
            "Flexible subscription engine with pause, skip, and customization options",
            "Integrated blog and recipe section with SEO-optimized content"],

        results: [
            "265% increase in organic search traffic",
            "Achieved #1 ranking for 'organic vegetables online India'",
            "220% boost in subscription sign-ups",
            "Customer retention rate of 85%",
            "Reduced delivery costs by 30% through route optimization"],

        testimonial: {
            text: "Site Map Genius understood our mission and translated it into a beautiful, functional platform. The subscription system works flawlessly, and our customers love the educational content. Sales have grown beyond our projections.",
            author: "Ananya Desai",
            position: "Founder, GreenEarth Organics",
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11672db96-1763301706352.png",
            avatarAlt: "Young Indian woman entrepreneur with warm smile in casual business attire with plants in background"
        }
    },
    {
        id: 6,
        title: "EduTech Academy - Learning Management System",
        category: "Web Development",
        description: "Comprehensive online learning platform with live classes, assessments, and progress tracking.",
        fullDescription: "EduTech Academy wanted to create a modern learning management system that would rival international platforms while catering to Indian education needs. We developed a full-featured LMS with live video classes, interactive assessments, progress analytics, discussion forums, and certificate generation. The platform supports multiple teaching formats including recorded lectures, live sessions, and hybrid learning models.",
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_100b79617-1764669441684.png",
        alt: "Modern classroom with students using laptops and tablets for digital learning with teacher at interactive whiteboard",
        liveUrl: "https://edutech.example.com",
        metrics: {
            trafficIncrease: "+310%",
            rankingImprovement: "Top 3",
            conversionBoost: "+240%"
        },
        technologies: ["Next.js", "TypeScript", "MongoDB", "WebRTC", "Socket.io", "AWS S3", "Razorpay"],
        timeline: "8 months",
        client: "EduTech Academy",
        challenges: [
            "Ensuring stable video streaming for large concurrent user sessions",
            "Creating engaging interactive assessments and quizzes",
            "Implementing comprehensive progress tracking and analytics",
            "Supporting multiple user roles (students, teachers, admins, parents)"],

        solutions: [
            "WebRTC implementation with fallback to HLS for scalable video delivery",
            "Gamified assessment system with instant feedback and explanations",
            "Real-time analytics dashboard with predictive performance insights",
            "Role-based access control with granular permission management"],

        results: [
            "25,000+ active students across 150+ courses",
            "310% increase in course enrollment",
            "Achieved top 3 rankings for online education keywords",
            "240% improvement in course completion rates",
            "99.5% platform uptime during peak exam seasons"],

        testimonial: {
            text: "The LMS platform Site Map Genius built has transformed how we deliver education. Students are more engaged, teachers have better tools, and our administrative efficiency has improved dramatically. It's been a game-changer for our academy.",
            author: "Dr. Suresh Kumar",
            position: "Director, EduTech Academy",
            avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12946c7bf-1763300744525.png",
            avatarAlt: "Distinguished Indian educator in formal attire with glasses and professional demeanor in academic setting"
        }
    }];


const stats = {
    totalProjects: 50,
    happyClients: 35,
    avgTrafficIncrease: "250%",
    avgRankingBoost: "Top 5"
};

export default function PortfolioInteractive() {
    const [isHydrated, setIsHydrated] = useState(false);
    const [activeCategory, setActiveCategory] = useState('All Projects');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    if (!isHydrated) {
        return (
            <div className="min-h-screen bg-background">
                <div className="animate-pulse">
                    <div className="h-64 bg-muted mb-8" />
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3, 4, 5, 6].map((i) =>
                                <div key={i} className="h-96 bg-muted rounded-xl" />
                            )}
                        </div>
                    </div>
                </div>
            </div>);

    }

    const categories = ['All Projects', 'Web Development', 'E-commerce', 'Mobile Development'];

    const filteredProjects = mockProjects.filter((project) => {
        const matchesCategory = activeCategory === 'All Projects' || project.category === activeCategory;
        const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            project.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const handleViewDetails = (projectId: number) => {
        const project = mockProjects.find((p) => p.id === projectId);
        if (project) {
            setSelectedProject(project);
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedProject(null), 300);
    };

    return (
        <>
            <section className="relative bg-gradient-to-br from-brand-primary via-brand-secondary to-brand-primary py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
                </div>

                <div className="relative max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                        Our Portfolio
                    </h1>
                    <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
                        Explore our collection of successful projects that have transformed businesses through innovative web development, mobile applications, and strategic brand visibility optimization.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <div className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg">
                            <span className="text-2xl font-bold text-white">50+</span>
                            <span className="text-sm text-white/80">Projects</span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg">
                            <span className="text-2xl font-bold text-white">35+</span>
                            <span className="text-sm text-white/80">Clients</span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg">
                            <span className="text-2xl font-bold text-white">250%</span>
                            <span className="text-sm text-white/80">Avg Growth</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
                <div className="max-w-7xl mx-auto">
                    <StatsSection stats={stats} />

                    <FilterBar
                        categories={categories}
                        activeCategory={activeCategory}
                        onCategoryChange={setActiveCategory}
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery} />


                    {filteredProjects.length === 0 ?
                        <div className="text-center py-16">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                                <Icon name="MagnifyingGlassIcon" size={32} className="text-text-secondary" />
                            </div>
                            <h3 className="text-xl font-semibold text-text-primary mb-2">No projects found</h3>
                            <p className="text-text-secondary">Try adjusting your filters or search query</p>
                        </div> :

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredProjects.map((project) =>
                                <ProjectCard
                                    key={project.id}
                                    project={project}
                                    onViewDetails={handleViewDetails} />

                            )}
                        </div>
                    }
                </div>
            </section>

            <ProjectModal
                project={selectedProject}
                isOpen={isModalOpen}
                onClose={handleCloseModal} />

        </>);

}